import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import config from '@/config';
import useGoBack from '@/hooks/useGoBack';
import GalleryOffersService from '@/services/galleryOffers.service';
import UserProfileImage from '@/shared-components/cards/components/UserProfileImage';
import InfoIcon from '@/shared-components/icons/components/public/InfoIcon';
import XIcon from '@/shared-components/icons/components/public/XIcon';
import ProfileContentLoader from '@/shared-components/loader/special-loaders/ProfileContentLoader';
import ProfileOptionsPageHeader from '@/shared-components/navigation/ProfileOptionsPageHeader';
import GenericPopup from '@/shared-components/popups/components/GenericPopup';
import NumberUtils from '@/utils/number.utils';

import './index.css';

function formatDate(value) {
  if (!value) return 'Unknown date';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown date';
  return date.toLocaleString();
}

function formatPrice(value, currency) {
  return NumberUtils.getOfferPrice(value, currency || 'USD') || '—';
}

function normalizeStatus(status) {
  const map = {
    open: 'Open',
    accepted: 'Accepted',
    declined: 'Declined',
    expired: 'Expired',
    cancelled: 'Cancelled',
  };
  return map[status] || 'Open';
}

function getStatusClass(status) {
  if (status === 'accepted') return 'is-accepted';
  if (status === 'declined' || status === 'cancelled') return 'is-declined';
  if (status === 'expired') return 'is-expired';
  return 'is-open';
}

export default function ProfileOffers() {
  const navigate = useNavigate();
  const goBackHandler = useGoBack('/profile');
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openOffersInfoPopup, setOpenOffersInfoPopup] = useState(false);

  useEffect(() => {
    let active = true;

    async function loadOffers() {
      setLoading(true);
      setError('');
      try {
        const { response, data } = await GalleryOffersService.getMyGalleryOffers({ limit: 50 });
        if (!response.ok) {
          throw new Error(data?.error_message || 'Failed to load offers');
        }
        if (!active) return;
        setOffers(Array.isArray(data?.offers) ? data.offers : []);
      } catch (err) {
        if (!active) return;
        setOffers([]);
        setError(err?.message || 'Failed to load offers');
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    void loadOffers();
    return () => {
      active = false;
    };
  }, []);

  const content = useMemo(() => {
    if (loading) {
      return (
        <div className="profile-offers__state profile-offers__state--loading">
          <ProfileContentLoader />
        </div>
      );
    }

    if (error) {
      return <p className="profile-offers__state profile-offers__state--error">{error}</p>;
    }

    if (offers.length === 0) {
      return (
        <p className="profile-offers__state profile-offers__state--empty">
          You do not have offers yet.
        </p>
      );
    }

    return (
      <div className="profile-offers__cards">
        {offers.map((offer) => {
          const artworkSlug = offer?.artwork?.slug || null;
          const artworkId = offer?.artwork_id || offer?.artwork?.artwork_id || offer?.artwork?._id;
          const artworkPath = artworkSlug
            ? `/artwork/${artworkSlug}`
            : artworkId
              ? `/artwork/${artworkId}`
              : null;
          const imageId = offer?.artwork?.image_id;
          const artworkSrc = imageId
            ? `${config.app.imageServiceDomain}/${imageId}/w=900`
            : null;

          return (
            <article key={offer.id} className="profile-offers__card">
              <div className="profile-offers__card-header">
                <div className="profile-offers__gallery">
                  <UserProfileImage
                    image={offer?.gallery?.image_id}
                    typeOfProfile="gallery"
                    className="profile-offers__gallery-image"
                  />
                  <div className="profile-offers__gallery-copy">
                    <strong className="font-size-medium">
                      {offer?.gallery?.name || 'Gallery'}
                    </strong>
                    <span className="font-size-small">
                      {offer?.gallery?.username ? `@${offer.gallery.username}` : ''}
                    </span>
                  </div>
                </div>
                <span className={`profile-offers__status ${getStatusClass(offer?.status)}`}>
                  {normalizeStatus(offer?.status)}
                </span>
              </div>

              <div
                className="profile-offers__artwork is-clickable"
                onClick={() => {
                  if (artworkPath) {
                    navigate(artworkPath, { state: { from: true } });
                  }
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    if (artworkPath) {
                      navigate(artworkPath, { state: { from: true } });
                    }
                  }
                }}
                role="button"
                tabIndex={0}
              >
                {artworkSrc ? (
                  <img
                    src={artworkSrc}
                    alt={offer?.artwork?.title || 'Offer artwork'}
                    className="profile-offers__artwork-image"
                  />
                ) : (
                  <div className="profile-offers__artwork-image profile-offers__artwork-placeholder" />
                )}
                <div className="profile-offers__artwork-copy">
                  <strong className="font-size-medium">
                    {offer?.artwork?.title || 'Untitled artwork'}
                  </strong>
                  <span className="font-size-small">
                    Sent {formatDate(offer?.sent_at)}
                  </span>
                  <span className="font-size-small">
                    Expires {formatDate(offer?.expires_at)}
                  </span>
                </div>
              </div>

              <div className="profile-offers__prices">
                <span className="font-size-small">
                  Current: {formatPrice(offer?.current_price, offer?.currency)}
                </span>
                <span className="font-size-small">
                  Offer: {formatPrice(offer?.offer_price, offer?.currency)}
                </span>
                <span className="font-size-small">
                  Discount: {typeof offer?.discount_pct === 'number' ? `${offer.discount_pct}%` : '—'}
                </span>
              </div>
            </article>
          );
        })}
      </div>
    );
  }, [error, loading, offers]);

  return (
    <div className="profile-offers-outter">
      <ProfileOptionsPageHeader
        title="Offers"
        onBack={goBackHandler}
        action={(
          <button
            type="button"
            className="profile-offers__info-trigger"
            onClick={() => setOpenOffersInfoPopup(true)}
            aria-label="Learn how offers work"
          >
            <InfoIcon className="profile-offers__info-icon" />
          </button>
        )}
      />
      {content}

      <GenericPopup
        open={openOffersInfoPopup}
        onBackgroundClick={() => setOpenOffersInfoPopup(false)}
        className="profile-offers__info-popup"
        opacity
      >
        <div className="profile-offers__info-popup-header">
          <h2 className="profile-offers__info-popup-title font-size-large">
            How to receive offers
          </h2>
          <XIcon
            className="profile-offers__info-popup-close"
            onClick={() => setOpenOffersInfoPopup(false)}
          />
        </div>

        <div className="profile-offers__info-popup-content">
          <p className="font-size-medium">
            Follow galleries and artists you genuinely like to unlock personalized offers in this
            section.
          </p>
          <p className="font-size-medium">
            When you follow a gallery, they can choose to send you limited-time offers on selected
            artworks.
          </p>
          <p className="font-size-medium">
            Some offers are sent to multiple collectors at once and are claimed on a first-come,
            first-served basis, so acting quickly matters before they expire.
          </p>
        </div>
      </GenericPopup>
    </div>
  );
}
